import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Users, ShieldCheck, Loader2, Eye, EyeOff, Check, X, AlertCircle, Mail, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import API from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const ROLE_ICON_COLOR = {
  owner: 'bg-amber-100 text-amber-700',
  account_manager: 'bg-blue-100 text-blue-700',
  finance: 'bg-emerald-100 text-emerald-700',
  legal: 'bg-purple-100 text-purple-700',
  read_only: 'bg-slate-100 text-slate-600',
};

const PASSWORD_RULES = [
  { key: 'len', label: '8+ characters', test: (v) => v.length >= 8 },
  { key: 'upper', label: 'One uppercase', test: (v) => /[A-Z]/.test(v) },
  { key: 'lower', label: 'One lowercase', test: (v) => /[a-z]/.test(v) },
  { key: 'num', label: 'One number', test: (v) => /[0-9]/.test(v) },
  { key: 'sym', label: 'One special character', test: (v) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(v) },
];

function isPasswordValid(pw) { return PASSWORD_RULES.every(r => r.test(pw)); }

export default function InviteAccept() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, loginWithToken, logout } = useAuth();
  const token = params.get('token');

  const [loadingPreview, setLoadingPreview] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  // Signup form for logged-out invitees
  const [signupForm, setSignupForm] = useState({ display_name: '', password: '', confirm: '', terms: false });
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (!token) { setLoadingPreview(false); return; }
    (async () => {
      try {
        const res = await API.get(`/invites/${token}`);
        setPreview(res.data);
      } catch (e) {
        setPreviewError(e.response?.data?.detail || 'Could not load invite');
      } finally { setLoadingPreview(false); }
    })();
  }, [token]);

  const acceptAsLoggedIn = async () => {
    setProcessing(true);
    try {
      await API.post('/buyer/settings/team/accept', { invite_token: token });
      toast.success('Invite accepted!');
      setResult('accepted');
      setTimeout(() => { window.location.href = '/agency'; }, 900);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept invite');
      setResult('error');
    } finally { setProcessing(false); }
  };

  const decline = async () => {
    setProcessing(true);
    try {
      await API.post('/buyer/settings/team/decline', { invite_token: token });
      toast.success('Invite declined');
      setResult('declined');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to decline invite');
    } finally { setProcessing(false); }
  };

  const signupAndAccept = async () => {
    if (!signupForm.display_name.trim()) { toast.error('Your name is required'); return; }
    if (!isPasswordValid(signupForm.password)) { toast.error('Password does not meet requirements'); return; }
    if (signupForm.password !== signupForm.confirm) { toast.error('Passwords do not match'); return; }
    if (!signupForm.terms) { toast.error('Please accept the Terms'); return; }
    setProcessing(true);
    try {
      const res = await API.post('/auth/signup-via-invite', {
        invite_token: token,
        password: signupForm.password,
        display_name: signupForm.display_name,
        terms_accepted: true,
      });
      loginWithToken(res.data.token, res.data.user);
      toast.success('Welcome to the team!');
      setResult('accepted');
      setTimeout(() => { window.location.href = '/agency'; }, 900);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Signup failed');
    } finally { setProcessing(false); }
  };

  // ── Rendering states ──
  if (authLoading || loadingPreview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (!token || previewError) {
    return (
      <Shell>
        <div className="text-center" data-testid="invite-error-card">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-slate-900 mb-1">Invite unavailable</h1>
          <p className="text-sm text-slate-500">{previewError || 'Check your link and try again.'}</p>
          <Link to="/" className="text-sm text-blue-600 underline mt-4 inline-block">Return home</Link>
        </div>
      </Shell>
    );
  }

  if (result === 'accepted') {
    return (
      <Shell>
        <div className="text-center" data-testid="invite-accepted">
          <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-slate-900 mb-1">Welcome to the team!</h1>
          <p className="text-sm text-slate-500">Redirecting you to the workspace…</p>
        </div>
      </Shell>
    );
  }
  if (result === 'declined') {
    return (
      <Shell>
        <div className="text-center" data-testid="invite-declined">
          <XCircle className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-slate-900 mb-1">Invite declined</h1>
          <p className="text-sm text-slate-500 mb-3">You've declined this invite.</p>
          <Link to="/"><Button variant="outline" className="border-slate-200">Go home</Button></Link>
        </div>
      </Shell>
    );
  }

  const { invite, agency, inviter, user_exists } = preview;
  const emailMismatch = user && user.email?.toLowerCase() !== invite.email.toLowerCase();

  return (
    <Shell>
      {/* Invite summary card */}
      <div data-testid="invite-accept-page">
        <div className="text-center mb-5">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-3">
            <Users className="w-8 h-8 text-blue-600" />
          </div>
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Team invitation</p>
          <h1 className="text-2xl font-semibold text-slate-900">
            Join {agency.name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            <span className="font-medium text-slate-700">{inviter.display_name || inviter.email}</span> invited <strong className="text-slate-700">{invite.email}</strong>
          </p>
        </div>

        {/* Role card */}
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-blue-500" />
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Your role</span>
          </div>
          <Badge className={`${ROLE_ICON_COLOR[invite.role] || 'bg-slate-100 text-slate-600'} text-xs mb-1.5`}>
            {invite.role_label}
          </Badge>
          <p className="text-xs text-slate-500">{invite.role_description}</p>
        </div>

        {/* Personal message */}
        {invite.personal_message && (
          <div className="rounded-xl bg-amber-50 border-l-3 border-amber-400 p-3 mb-4">
            <p className="text-[10px] text-amber-700 font-semibold uppercase tracking-wide mb-1">Note from {inviter.display_name || inviter.email}</p>
            <p className="text-sm text-amber-900 italic">"{invite.personal_message}"</p>
          </div>
        )}

        {/* Action area */}
        {!user ? (
          user_exists ? (
            <div className="rounded-xl border border-slate-200 p-4 text-center" data-testid="invite-existing-user">
              <Mail className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-sm text-slate-700 mb-3">
                You already have a Mylikeness account as <strong>{invite.email}</strong>. Sign in to accept.
              </p>
              <Link to={`/login?redirect=${encodeURIComponent(`/invite/accept?token=${token}`)}`}>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white w-full h-10" data-testid="invite-login-btn">
                  Sign in to accept
                </Button>
              </Link>
            </div>
          ) : (
            <InviteSignupForm
              email={invite.email}
              form={signupForm}
              setForm={setSignupForm}
              showPw={showPw}
              setShowPw={setShowPw}
              onSubmit={signupAndAccept}
              processing={processing}
              onDecline={decline}
            />
          )
        ) : emailMismatch ? (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-center" data-testid="invite-email-mismatch">
            <AlertCircle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
            <p className="text-sm text-slate-700 mb-1">
              This invite was sent to <strong>{invite.email}</strong>.
            </p>
            <p className="text-xs text-slate-500 mb-3">
              You're signed in as <strong>{user.email}</strong>. Sign out and try again.
            </p>
            <Button onClick={() => { logout(); window.location.reload(); }}
              variant="outline" className="w-full h-9 text-xs" data-testid="invite-signout-btn">
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
            </Button>
          </div>
        ) : (
          <div className="flex gap-3">
            <Button onClick={acceptAsLoggedIn} disabled={processing}
              data-testid="accept-invite-btn"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-10">
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Accept invitation
            </Button>
            <Button onClick={decline} disabled={processing}
              data-testid="decline-invite-btn"
              variant="outline" className="flex-1 border-slate-200 text-slate-600 h-10">
              Decline
            </Button>
          </div>
        )}

        <p className="text-[10px] text-slate-400 text-center mt-5">
          {user ? <>Signed in as {user.email}. </> : null}
          Invitations expire 7 days after being sent.
        </p>
      </div>
    </Shell>
  );
}

// ── Logged-out signup form scoped to the invite ──
function InviteSignupForm({ email, form, setForm, showPw, setShowPw, onSubmit, processing, onDecline }) {
  const pwValid = isPasswordValid(form.password);
  const match = form.password && form.password === form.confirm;

  return (
    <div data-testid="invite-signup-form" className="space-y-3">
      <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-3 mb-1">
        <p className="text-[11px] text-blue-900">
          <strong>One-time setup.</strong> We'll create your account so you can jump straight into the workspace. No separate signup needed.
        </p>
      </div>

      <div>
        <Label className="text-[11px] text-slate-600 font-medium">Email</Label>
        <Input value={email} readOnly disabled
          data-testid="invite-signup-email"
          className="h-10 mt-1 text-sm bg-slate-50 text-slate-500 cursor-not-allowed" />
        <p className="text-[10px] text-slate-400 mt-1">Locked — this is the address the invite was sent to.</p>
      </div>

      <div>
        <Label className="text-[11px] text-slate-600 font-medium">Your full name</Label>
        <Input value={form.display_name}
          data-testid="invite-signup-name"
          onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
          placeholder="Sam Finance" className="h-10 mt-1 text-sm" />
      </div>

      <div>
        <Label className="text-[11px] text-slate-600 font-medium">Password</Label>
        <div className="relative">
          <Input type={showPw ? 'text' : 'password'} value={form.password}
            data-testid="invite-signup-password"
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            placeholder="At least 8 characters" className="h-10 mt-1 text-sm pr-9" />
          <button type="button" onClick={() => setShowPw(!showPw)}
            className="absolute right-3 top-1/2 translate-y-[3px] text-slate-400 hover:text-slate-600">
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {form.password && (
          <div className="mt-1.5 grid grid-cols-2 gap-0.5">
            {PASSWORD_RULES.map(r => {
              const ok = r.test(form.password);
              return (
                <div key={r.key} className="flex items-center gap-1 text-[10px]">
                  {ok ? <Check className="w-3 h-3 text-emerald-500" /> : <X className="w-3 h-3 text-slate-300" />}
                  <span className={ok ? 'text-emerald-600' : 'text-slate-400'}>{r.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <Label className="text-[11px] text-slate-600 font-medium">Confirm password</Label>
        <Input type={showPw ? 'text' : 'password'} value={form.confirm}
          data-testid="invite-signup-confirm"
          onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
          placeholder="Re-enter password"
          className={`h-10 mt-1 text-sm ${form.confirm && !match ? 'border-red-300' : ''}`} />
        {form.confirm && !match && (
          <p className="text-[10px] text-red-500 mt-1">Passwords don't match</p>
        )}
      </div>

      <label className="flex items-start gap-2 p-3 rounded-lg border border-slate-200 bg-white cursor-pointer hover:border-slate-300">
        <input type="checkbox" checked={form.terms}
          data-testid="invite-signup-terms"
          onChange={e => setForm(f => ({ ...f, terms: e.target.checked }))}
          className="mt-0.5 rounded border-slate-300" />
        <span className="text-[11px] text-slate-600">
          I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Terms</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>.
        </span>
      </label>

      <div className="flex gap-2 pt-1">
        <Button onClick={onSubmit} disabled={processing || !pwValid || !match || !form.display_name.trim() || !form.terms}
          data-testid="invite-signup-submit"
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-10">
          {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
          Join {}
        </Button>
        <Button onClick={onDecline} disabled={processing}
          data-testid="invite-decline-btn"
          variant="outline" className="h-10 border-slate-200 text-slate-500 px-4">
          Decline
        </Button>
      </div>

      <p className="text-[10px] text-slate-400 text-center">
        Already have a Mylikeness account as <strong>{email}</strong>?{' '}
        <Link to={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`} className="text-blue-600 underline">Sign in instead</Link>.
      </p>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-10">
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 max-w-md w-full">
        <div className="text-center mb-1">
          <p className="font-heading text-base font-semibold text-slate-900">Mylikeness</p>
        </div>
        <div className="mt-3">{children}</div>
      </motion.div>
    </div>
  );
}
